import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { DataProvider } from "projects/packcert-web/src/app/providers/data.provider";
import { Subject } from "rxjs";
import { Message, UtilsService, SpinnerService } from "shared-components";
import { GestionService } from "../../../../services/gestion.service";

@Component({
  selector: 'app-new-edit-proteccion',
  templateUrl: './new-edit-proteccion.component.html',
})

export class NewEditProteccionComponent implements OnInit {
    public newEditForm: UntypedFormGroup;
    public randomId = Math.random();
    public disableEstadosProteccion: Subject<boolean> = new Subject();
    public isEditable = false;
    public deleteModal = false;

    public showError: boolean;
    public mensajes = new Array<Message>();

    @Input() proteccion; // solo se pasa al editar
    @Input() denominacionId; // se pasa igualmente, tanto al crear como al editar

    @Output() refresh = new EventEmitter<boolean>();
    @Output() mensaje = new EventEmitter<string>(); // sin traducir

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _dataProvider: DataProvider,
        private readonly utilsService: UtilsService,
        private readonly _spinnerService: SpinnerService,
        private readonly _gestionService: GestionService
    ) {}

    ngOnInit(): void {
        this.newEditForm = this._fb.group({
            titular: ['', Validators.required],
            ambito: ['', Validators.required],
            estado: ['', Validators.required],
            fechaInicio: ['', Validators.required],
            fechaFin: ['', Validators.required],
        });

        if (this.proteccion) {
            this.patchEditForm();
            this.newEditForm.disable();
            this.disableEstadosProteccion.next(true);
        } else {
            this.disableEstadosProteccion.next(false); 
            // Estado Vigente por defecto al crear
            const defaultEstado = this._dataProvider.estadosProteccion.find(el => el.id === 1);
            this.newEditForm.get('estado').patchValue(defaultEstado);
        }
    }

    public get estadosProteccion() {
        return this._dataProvider.estadosProteccion;
    }

    private patchEditForm() {
        const currentEstado = this._dataProvider.estadosProteccion.find(el => el.id === this.proteccion.estadoId);
        this.newEditForm.patchValue({
            titular: this.proteccion.titular,
            ambito: this.proteccion.ambitoRegistro,
            estado: currentEstado,
            fechaInicio: this.proteccion.inicioVigencia ? new Date(this.proteccion.inicioVigencia) : null,
            fechaFin: this.proteccion.finVigencia ? new Date(this.proteccion.finVigencia) : null,
        });
    }

    public cancel() {
        if (!this.proteccion) {
            this.refresh.emit(false);
        } else {
            this.isEditable = false;
            this.newEditForm.disable();
            this.disableEstadosProteccion.next(true);
            this.patchEditForm();
        }
    }

    public enableEdit() {
        this.isEditable = true;
        this.newEditForm.enable();
        this.disableEstadosProteccion.next(false);
    }

    private checkDates(inicio, fin) {
        const inicioD = new Date(inicio);
        const finD = new Date(fin);
        return inicioD <= finD;
    }

    private clearDatesValidators() {
        if (this.newEditForm.get('fechaFin').errors && this.newEditForm.get('fechaFin').value) {
            this.newEditForm.get('fechaFin').clearValidators();
            this.newEditForm.get('fechaFin').updateValueAndValidity();
        }
        if (this.newEditForm.get('fechaInicio').errors && this.newEditForm.get('fechaInicio').value) {
            this.newEditForm.get('fechaInicio').clearValidators();
            this.newEditForm.get('fechaInicio').updateValueAndValidity();
        }
    }

    private validDates() {
        if (this.newEditForm.get('fechaInicio').value && this.newEditForm.get('fechaFin').value) {
            if (this.checkDates(this.newEditForm.get('fechaInicio').value, this.newEditForm.get('fechaFin').value)) {
                this.clearDatesValidators();
            } else {
                this.newEditForm.get('fechaInicio').setErrors(['Invalid date']);
                this.newEditForm.get('fechaFin').setErrors(['Invalid date']);
            }
            if (this.newEditForm.get('fechaInicio').invalid || this.newEditForm.get('fechaFin').invalid) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.fecha.error.finAnteriorInicio' });
            } else {
                this.showError = false;
                this.mensajes = [];
            }
        } else {
            this.clearDatesValidators();
            this.showError = false;
            this.mensajes = [];
        }
    }

    private createOrUpdate(filters) {
        this._spinnerService.showSpinner();
        this._gestionService.createOrUpdateProteccion(filters).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            if (!this.proteccion) {
                this.mensaje.emit('global.success.itemCreated');
            } else {
                this.mensaje.emit('global.success.itemUpdated');
            }
            
            this.refresh.emit(true);
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        });
    }

    public save() {
        this.validDates();

        if (this.newEditForm.invalid) {
            const emptyFields = Object.values(this.newEditForm.controls).some(
                control => control.errors?.['required']
            );

            if (emptyFields) {
                this.showError = true;
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }
        } else {
            const proteccionInfo = {
                denominacionId: this.denominacionId,
                titular: this.newEditForm.get('titular').value || '',
                ambitoRegistro: this.newEditForm.get('ambito').value || '',
                estadoId: this.newEditForm.get('estado')?.value?.id,
                inicioVigencia: this.newEditForm.get('fechaInicio')?.value ?
                    this.utilsService.parseGMTtoYYYYMMDD(this.newEditForm.get('fechaInicio').value) : '',
                finVigencia: this.newEditForm.get('fechaFin')?.value ?
                    this.utilsService.parseGMTtoYYYYMMDD(this.newEditForm.get('fechaFin').value) : '',
            };

            this.createOrUpdate(proteccionInfo);
        }
    }

    public askDelete() {
        this.deleteModal = true;
    }

    public cancelDelete() {
        this.deleteModal = false;
    }

    public delete() {
        this._spinnerService.showSpinner();
        this._gestionService.deleteProteccion(this.denominacionId).toPromise().finally(() => {
            this._spinnerService.hideSpinner();
        }).then((resp) => {
            this.mensaje.emit('global.success.itemDeleted');
            this.refresh.emit(true);
        }, (error) => {
            this.showError = true;
            this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
        });
    }
}